import React, { useMemo, useCallback, useState, useEffect, forwardRef } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import Grid from '@material-ui/core/Grid'

import { reloadSchema } from '@galo-monitor/forms'
import rawMaterialSchema from '@galo-monitor/forms/schemas/farm/rawMaterial.schema'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { RawMaterialsStocksActions } from '@galo-monitor/stores/rawMaterialsStocks'
import Form from '@galo-monitor/web-components/Form'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'
import RadioGroup from '@galo-monitor/web-components/RadioGroup'


const RawMaterialForm = forwardRef((props, formRef) => {
  const { rawMaterial, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const [selectedRawMaterialType, setSelectedRawMaterialType] = useState(rawMaterial?.agriculturalInputSubTypeId ?? '')

  const { data: rawMaterialData, isValidating: isValidatingRawMaterials } = useGaloMonitorApi(rawMaterial.id ? `/farms/agricultures/inputs/${ rawMaterial.id }` : null)
  const { data: rawMaterialTypes, isValidating: isValidatingRawMaterialsTypes } = useGaloMonitorApi('/farms/agricultures/types/inputs')
  const { data: rawMaterialSubTypes, isValidating: isValidatingRawMaterialsSubTypes } = useGaloMonitorApi(selectedRawMaterialType ? `/farms/agricultures/inputs/sub-types/${ selectedRawMaterialType }` : null)

  const typeOptions = useMemo(
    () => map(rawMaterialTypes, type => ({
      label: type.name,
      value: String(type.id)
    })),
    [rawMaterialTypes]
  )

  const subTypeOptions = useMemo(
    () => map(rawMaterialSubTypes, subType => ({
      label: subType.name,
      value: String(subType.id)
    })),
    [rawMaterialSubTypes]
  )

  const modeOptions = useMemo(
    () => [
      { label: t('liquid'), value: 'liquid' },
      { label: t('solid'), value: 'solid' }
    ],
    [t]
  )

  const handleChangeRawMaterialType = useCallback(
    () => reloadSchema(formRef, true),
    [formRef]
  )

  const handleSubmit = useCallback(
    (data) => {
      setLoading(true)
      dispatch(RawMaterialsStocksActions.saveRawMaterial(
        {
          ...rawMaterial,
          ...data
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('stock', { howMany: 1 }),
            action: toLower(t(isEmpty(rawMaterial) ? 'created' : 'updated', { gender: 'female', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [dispatch, rawMaterial, onError, onSuccess, setLoading, snackbar, t]
  )

  useEffect(() => {
    if (!isEmpty(rawMaterialData) && !isValidatingRawMaterials) {
      const { agriculturalInputTypeId, agriculturalInputSubTypeId } = rawMaterialData
      formRef.current.setFieldValue('agricultural_input_type_id', String(agriculturalInputTypeId))

      if (selectedRawMaterialType === '2' && formRef.current.getFieldRef('agricultural_input_sub_type_id')) {
        formRef.current.setFieldValue('agricultural_input_sub_type_id', agriculturalInputSubTypeId.toString())
      }

      setLoading(false)
    }
  }, [rawMaterialData, setLoading, formRef, selectedRawMaterialType, isValidatingRawMaterials, isValidatingRawMaterialsSubTypes])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => rawMaterial.id && setLoading(true), [])

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ rawMaterialSchema }
      onSubmit={ handleSubmit }
    >
      <Grid container spacing={ 2 }>
        <Grid item xs={ 12 }>
          <InputText
            name="name"
            label={ t('name') }
            defaultValue={ rawMaterial?.name ?? '' }
            disabled={ loading || isValidatingRawMaterials }
            fullWidth
          />
        </Grid>
        <Grid item xs={ 12 } style={ { marginTop: 15 } }>
          <InputSelect
            name="agricultural_input_type_id"
            label={ t('type', { howMany: 2 }) }
            options={ typeOptions }
            disabled={ loading || isValidatingRawMaterialsTypes }
            onChange={ handleChangeRawMaterialType }
            onFieldValueChange={ setSelectedRawMaterialType }
            fullWidth
          />
        </Grid>
        {selectedRawMaterialType === '2' ? (
          <>
            <Grid item xs={ 12 } style={ { marginTop: 15 } }>
              <InputSelect
                name="agricultural_input_sub_type_id"
                label={ t('fertilizer type') }
                options={ subTypeOptions }
                defaultValue={ rawMaterial?.agriculturalInputSubTypeId ?? '' }
                disabled={ loading || isValidatingRawMaterialsSubTypes }
                onChange={ handleChangeRawMaterialType }
                fullWidth
              />
            </Grid>
            <Grid item xs={ 12 } style={ { marginTop: 15 } }>
              <RadioGroup
                label={ t('product type') }
                labelSize="small"
                variant="row"
                name="type_product"
                defaultValue={ rawMaterial?.typeProduct ?? '' }
                options={ modeOptions }
                disabled={ loading }
                fullWidth
              />
            </Grid>
          </>
        ) : null}
      </Grid>
    </Form>
  )
})

RawMaterialForm.propTypes = {
  rawMaterial: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

RawMaterialForm.defaultProps = {
  rawMaterial: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default RawMaterialForm
